import { useEffect, useState, type FC } from 'react';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { useFeatureVersion } from '@hooks/useFeatureVersion';
import { FeatureVersionCard } from './FeatureVersionCard';
import { FeatureInvitationModal } from './FeatureInvitationModal';
import { MENU_FEATURE_KEY } from '@utils/featureRollout/constants';
import { dismissInvitation, isInvitationDismissed } from '@utils/featureRollout/invitationPrefs';

// Conector del framework de rollout para `menu-v2`: cablea el hook + la identidad root
// con la card reutilizable y el modal contextual de primera vez. Se monta en el shell del
// menú (sobre la página resuelta).
//
// La card (banner discreto) cubre el estado persistente: invitación en control / regreso
// en treatment / auto-oculta en GA. El modal contextual REFUERZA la visibilidad la PRIMERA
// vez que un comercio elegible entra al menú (control + experimento activo, aún no aceptó),
// con "no volver a preguntar" persistido por comercio. Es no-bloqueante (se puede cerrar).
//
// El switch lo dispara el root; no-root recibe aviso suave (card y modal lo manejan).

const MenuVersionInvitation: FC = () => {
  const { variant, canRevert, gaForced, isEligibleInvitation, menuState, switchTo, revert, markInvitationShown } =
    useFeatureVersion(MENU_FEATURE_KEY);
  const { isRoot } = useSubscription();

  // SUPERFICIES INTRUSIVAS (modal + card "Probar") SOLO en `never_tried`: un comercio que ya
  // probó (exploring / adopted / reverted_sticky) NO debe ser re-invitado al instante tras
  // revertir — eso es badgering y sesga la métrica (aceptación por hartazgo). El cooldown/cap
  // durables (21d→45d→sticky, máx 3) los gobierna el backend vía `invite_surface`; este gate
  // client-side es la mitigación inmediata leyendo el `menu_state` que ya viaja en el GET.
  // El BADGE del nav (canal pull, isEligibleInvitation) se mantiene como acceso permanente.
  const isFirstTime = !menuState || menuState === 'never_tried';
  const canInviteActive = isFirstTime && isEligibleInvitation && !isInvitationDismissed(MENU_FEATURE_KEY);

  // El modal se abre una vez por montaje del shell si procede invitar activamente.
  // Decisión SÍNCRONA (localStorage + menu_state ya hidratado) → sin parpadeo.
  const [showModal, setShowModal] = useState(() => canInviteActive);

  // La elegibilidad/estado puede llegar después de hidratar. Si pasa a invitable y no estaba
  // descartado, abrimos el modal una sola vez.
  const openedRef = useState(() => ({ done: false }))[0];
  useEffect(() => {
    if (openedRef.done) return;
    if (canInviteActive) {
      openedRef.done = true;
      setShowModal(true);
    }
  }, [canInviteActive, openedRef]);

  const closeModal = () => setShowModal(false);
  const neverAsk = () => {
    dismissInvitation(MENU_FEATURE_KEY);
    setShowModal(false);
  };
  const acceptFromModal = () => {
    switchTo('treatment', 'menu_modal');
    setShowModal(false);
  };

  return (
    <>
      {/* En treatment el indicador "estás en la versión nueva" + volver vive como CHIP en el
          header de MenuV2 (MenuVersionStatusChip), NO como banner sobre el contenido. Aquí solo
          se renderiza la INVITACIÓN (control): la oferta a probar la nueva versión. */}
      {variant !== 'treatment' && isFirstTime && (
        <div style={{ marginBottom: 12 }}>
          <FeatureVersionCard
            variant={variant}
            canRevert={canRevert}
            gaForced={gaForced}
            isRoot={Boolean(isRoot)}
            onTry={() => switchTo('treatment', 'menu_shell')}
            onRevert={() => revert('menu_shell')}
            onShown={() => markInvitationShown('menu_shell')}
          />
        </div>
      )}
      {showModal && (
        <FeatureInvitationModal
          isRoot={Boolean(isRoot)}
          onTry={acceptFromModal}
          onDismiss={closeModal}
          onNeverAsk={neverAsk}
          onShown={() => markInvitationShown('menu_modal')}
        />
      )}
    </>
  );
};

export default MenuVersionInvitation;
